import { useFragment, useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import { TabLabelRows } from 'src/components/GlobalTabs/tab-labels/tab-label-rows';
import { gql } from 'src/gql';
import { useGlobalStore } from 'src/store';
import { ClientTabEntity, TabType, useGlobalTabs } from 'src/utils/hooks';

const ClientLabelQuery = gql(`
  query ClientLabelQuery($params: QueryParamsWithSearch) @entity(name: "Client") {
    getClient(queryParams: $params) {
      records {
        id
        ...ClientLabel_Client
      }
    }
  }
`);

const ClientLabelFragment = gql(`
  fragment ClientLabel_Client on Client {
    id
    clientId
    partnerId
    firstName
    lastName
    partner {
      id
      businessName
    }
  }
`);

type ClientLabelProps = ClientTabEntity['entity'];

type ClientLabelState = ClientLabelProps & { partnerName: string };

export const ClientLabel = (props: ClientLabelProps) => {
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();

  const result = useQuery(ClientLabelQuery, {
    variables: {
      params: {
        filters: [
          {
            attribute: 'clientId',
            operator: '=',
            value: props.clientId,
          },
        ],
        includeDeleted: true,
      },
    },
    skip: !props.clientId,
  });

  const { data: selectedClient, complete } = useFragment({
    fragment: ClientLabelFragment,
    fragmentName: 'ClientLabel_Client',
    from: {
      __typename: 'Client',
      id: props.clientId,
    },
  });

  const [state, setState] = useState<ClientLabelState>({
    ...props,
    partnerName: '',
  });

  const { activeTab, closeTab, findTab } = useGlobalTabs();
  const isActive = activeTab?.id === props.clientId && activeTab?.type === TabType.Client;

  useEffect(() => {
    // gate on the query settling; the fragment stays incomplete both while loading
    // and when the record doesn't exist.
    if (result.loading) return;

    if (complete) {
      setState({
        id: selectedClient.clientId,
        partnerName: selectedClient.partner?.businessName ?? '',
        partnerId: selectedClient.partnerId,
        clientId: selectedClient.clientId,
      });
    } else if (!isActive) {
      // Close inactive tab if there's no client with specified Id. It is important to avoid
      // closing active tab as it is handled by ClientExpanded component at the moment when
      // client doesn't exist anymore, but inactive tabs aren't linked to ClientExpanded.
      const tab = findTab(props.clientId, TabType.Client);
      if (tab) {
        closeTab(tab);
      }
    }
  }, [isActive, findTab, result.loading, complete, props.clientId, closeTab, selectedClient]);

  const { partnerName } = state;

  const clientLabel = [selectedClient?.firstName, selectedClient?.lastName].join(' ').trim();

  // A client has no friendly id, so the reference row carries the partner alone.
  return (
    <TabLabelRows
      tooltip={complete ? clientLabel : undefined}
      overline={selectedPartnerId ? undefined : partnerName}
      title={complete ? clientLabel : undefined}
    />
  );
};
